import {
  eventDurationMinutes,
  eventStartsInTheFuture,
  isLiveZoomSessionId,
  uniqueEventHostIds,
  uniqueMarbleCeStates,
  workshopCeApplicableCopy,
  workshopCeStatesLabel,
} from "@marble/core/modules/event/utils";
import { escapeAttr, escapeHtml } from "@marble/core/utils/html";
import {
  WORKSHOP_DURATION_MINUTE_OPTIONS,
  type WorkshopLanguage,
} from "@/lib/modules/events/workshops/constants";
import type { AdminWorkshopStats } from "@/lib/modules/events/workshops/types";
import {
  EventAccess,
  EventStatus,
  EventType,
  EventZoomSessionType,
  Language,
  type State,
} from "@prisma/client";
import { DateTime } from "luxon";

export {
  eventDurationMinutes,
  eventStartsInTheFuture,
  uniqueEventHostIds,
  uniqueMarbleCeStates,
  workshopCeApplicableCopy,
  workshopCeStatesLabel,
};

export const eventEndFromStart = (start: Date, durationMinutes: number): Date =>
  new Date(start.getTime() + durationMinutes * 60_000);

export const eventOffersCeCredit = (states: State[]): boolean =>
  states.length > 0;

/** Workshop copy only exists in English and Spanish. */
export const workshopFormLanguage = (language: Language): WorkshopLanguage =>
  language === Language.SPANISH ? Language.SPANISH : Language.ENGLISH;

export type WorkshopParticipantStatRow = {
  userId: number | null;
  attended: boolean;
  schoolId: number | null;
  schoolState: State | null;
};

export type WorkshopRecordingStatRow = {
  userId: number | null;
  participantId: number | null;
};

/** Live attendees whose school is in a CE-offered state. */
export const workshopAttendeeQualifiesForCe = (
  attended: boolean,
  schoolState: State | null,
  ceStates: State[],
): boolean => attended && schoolState != null && ceStates.includes(schoolState);

export const workshopStatsFromRows = (
  participants: WorkshopParticipantStatRow[],
  recordings: WorkshopRecordingStatRow[],
  ceStates: State[],
  attendanceSyncedAt: Date | null,
): AdminWorkshopStats => {
  const ceOffered = eventOffersCeCredit(ceStates);
  let attendeeCount = 0;
  let registrantsWithMarbleAccount = 0;
  let attendeesWithMarbleAccount = 0;
  let ceQualifiedCount = 0;
  const schoolIds = new Set<number>();

  for (const participant of participants) {
    const hasAccount = participant.userId != null;
    if (hasAccount) {
      registrantsWithMarbleAccount += 1;
    }
    if (participant.schoolId != null) {
      schoolIds.add(participant.schoolId);
    }
    if (participant.attended) {
      attendeeCount += 1;
      if (hasAccount) {
        attendeesWithMarbleAccount += 1;
      }
      if (
        workshopAttendeeQualifiesForCe(true, participant.schoolState, ceStates)
      ) {
        ceQualifiedCount += 1;
      }
    }
  }

  const registrantCount = participants.length;
  const attendanceSynced = attendanceSyncedAt != null;
  return {
    registrantCount,
    attendeeCount,
    registrantsWithMarbleAccount,
    attendeesWithMarbleAccount,
    uniqueSchoolCount: schoolIds.size,
    noShowCount: attendanceSynced ? registrantCount - attendeeCount : 0,
    attendanceRatePercent:
      !attendanceSynced || registrantCount === 0
        ? null
        : Math.round((attendeeCount / registrantCount) * 100),
    ceOffered,
    ceQualifiedCount: ceOffered ? ceQualifiedCount : 0,
    uniqueRecordingViewerCount: recordings.length,
    recordingViewersWithMarbleAccount: recordings.filter(
      (row) => row.userId != null,
    ).length,
    recordingViewersWhoRegistered: recordings.filter(
      (row) => row.participantId != null,
    ).length,
  };
};

/** Private workshops are Zoom meetings; public workshops are webinars. */
export const zoomSessionTypeForAccess = (
  access: EventAccess,
): EventZoomSessionType =>
  access === EventAccess.PRIVATE
    ? EventZoomSessionType.MEETING
    : EventZoomSessionType.WEBINAR;

export const workshopCeCreditBadgeLabel = (
  states: State[],
  compact = false,
): string | null => {
  const label = workshopCeStatesLabel(states);
  if (!label) {
    return null;
  }
  return compact ? `CE · ${label}` : `CE credit · ${label}`;
};

export const workshopCeAttendanceCopy = (states: State[]): string | null => {
  const applicable = workshopCeApplicableCopy(states);
  if (!applicable) {
    return null;
  }
  return `${applicable} Full live attendance is required.`;
};

export const workshopHostLabel = (host: {
  name: string;
  email: string | null;
}): string => {
  const email = host.email?.trim();
  return email ? `${host.name} (${email})` : host.name;
};

export const workshopOwnerLabel = (owner: {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}): string => {
  const name = workshopPersonName(owner);
  const email = owner.email?.trim();
  if (name && email) {
    return `${name} (${email})`;
  }
  return name || email || "Admin";
};

export const workshopPersonName = (person: {
  firstName: string | null;
  lastName: string | null;
}): string | null => {
  const name = [person.firstName?.trim(), person.lastName?.trim()]
    .filter(Boolean)
    .join(" ");
  return name || null;
};

/** Admin-only route that fetches the Zoom playback URL at click time. */
export const workshopRecordingApiPath = (eventId: number): string =>
  `/api/admin/workshops/${eventId}/recording`;

/** Same-origin .ics download. Private events need the registration token. */
export const workshopCalendarPath = (
  eventId: number,
  token?: string,
): string =>
  token
    ? `/events/${eventId}/calendar?token=${encodeURIComponent(token)}`
    : `/events/${eventId}/calendar`;

/** Webinar attendees watch; meeting attendees join. */
export const workshopJoinCtaLabel = (
  zoomSessionType: EventZoomSessionType,
): string =>
  zoomSessionType === EventZoomSessionType.WEBINAR ? "Watch in Zoom" : "Join";

/** Zoom web portal page for this webinar or meeting's settings and reports. */
export const workshopZoomEventUrl = (
  zoomId: string | null,
  sessionType: EventZoomSessionType,
): string | null => {
  if (!isLiveZoomSessionId(zoomId)) {
    return null;
  }
  const path =
    sessionType === EventZoomSessionType.WEBINAR ? "webinar" : "meeting";
  return `https://zoom.us/${path}/${encodeURIComponent(zoomId)}`;
};

/** Zoom web portal profile for a host's Zoom user id. */
export const hostZoomProfileUrl = (zoomId: string): string =>
  `https://zoom.us/user/${encodeURIComponent(zoomId)}/profile`;

const ZOOM_ATTENDEE_LOG_CSV_HEADERS = [
  "Name (original name)",
  "Email",
  "Join time",
  "Leave time",
  "Duration (minutes)",
  "Guest",
  "Recording disclaimer response",
  "In waiting room",
] as const;

/**
 * Zoom dashboard participant-report columns. Times are in the workshop
 * timezone, duration is minutes, Yes/No match Zoom's export.
 */
export const workshopAttendeeLogCsvRows = (
  entries: Record<string, unknown>[],
  timezone: string,
): string[][] => [
  [...ZOOM_ATTENDEE_LOG_CSV_HEADERS],
  ...entries.map((entry) => zoomDashboardAttendeeRow(entry, timezone)),
];

const zoomDashboardAttendeeRow = (
  entry: Record<string, unknown>,
  timezone: string,
): string[] => {
  const join = parseZoomInstant(entry.join_time, timezone);
  const leave = parseZoomInstant(entry.leave_time, timezone);
  return [
    attendeeLogCsvCell(entry.name),
    attendeeLogCsvCell(entry.user_email),
    formatZoomCsvTime(join),
    formatZoomCsvTime(leave),
    durationMinutesForZoomCsv(entry.duration, join, leave),
    zoomYesNo(isZoomGuest(entry)),
    recordingDisclaimerForZoomCsv(entry),
    zoomYesNo(entry.status === "in_waiting_room"),
  ];
};

const parseZoomInstant = (
  value: unknown,
  timezone: string,
): DateTime | null => {
  if (typeof value !== "string" || value.length === 0) {
    return null;
  }
  const date = DateTime.fromISO(value, { setZone: true }).setZone(timezone);
  return date.isValid ? date : null;
};

/** Zoom's participant CSV: `10/27/2026 10:52:44 AM` in the session timezone. */
const formatZoomCsvTime = (date: DateTime | null): string =>
  // eslint-disable-next-line marble/no-unzoned-date-render -- Zoom CSV wire format; the DateTime is already setZone'd to the workshop timezone, and Zoom's column does not carry a zone label.
  date ? date.toFormat("M/d/yyyy h:mm:ss a") : "";

const durationMinutesForZoomCsv = (
  duration: unknown,
  join: DateTime | null,
  leave: DateTime | null,
): string => {
  if (join && leave) {
    return String(Math.round(leave.diff(join, "minutes").minutes));
  }
  if (typeof duration !== "number" || !Number.isFinite(duration)) {
    return "";
  }
  // Zoom's report API returns duration in seconds.
  return String(Math.round(duration / 60));
};

const isZoomGuest = (entry: Record<string, unknown>): boolean => {
  if (typeof entry.guest === "boolean") {
    return entry.guest;
  }
  if (typeof entry.internal_user === "boolean") {
    return !entry.internal_user;
  }
  const loggedInId =
    (typeof entry.participant_user_id === "string" &&
      entry.participant_user_id) ||
    (typeof entry.id === "string" && entry.id);
  return !loggedInId;
};

const recordingDisclaimerForZoomCsv = (
  entry: Record<string, unknown>,
): string => {
  const response = entry.recording_disclaimer_response;
  if (typeof response === "string" && response.length > 0) {
    return response;
  }
  if (entry.recording_consent === true) {
    return "OK";
  }
  return "No Response";
};

const zoomYesNo = (value: boolean): string => (value ? "Yes" : "No");

const attendeeLogCsvCell = (value: unknown): string => {
  if (value == null) {
    return "";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

export const eventRegistrationIsOpen = (
  event: { status: EventStatus; eventEndAt: Date },
  now = new Date(),
): boolean =>
  event.status === EventStatus.ACTIVE &&
  event.eventEndAt.getTime() >= now.getTime();

export const durationOptionsForMinutes = (current: number): number[] => {
  const options: number[] = [...WORKSHOP_DURATION_MINUTE_OPTIONS];
  if (!options.includes(current)) {
    options.push(current);
    options.sort((a, b) => a - b);
  }
  return options;
};

const isPdWorkshop = (type: EventType) =>
  type === EventType.PROFESSIONAL_DEVELOPMENT_WORKSHOP;

const workshopShareIntro = (
  language: "ENGLISH" | "SPANISH",
  type: EventType,
): string => {
  if (language === "SPANISH") {
    return isPdWorkshop(type)
      ? "Un colega quiere compartir con usted este taller gratuito"
      : "Su escuela quiere compartir con usted este taller gratuito";
  }
  return isPdWorkshop(type)
    ? "A colleague wants to share this free workshop with you"
    : "Your school wants to share this free workshop with you";
};

const workshopShareCeSentence = (
  language: "ENGLISH" | "SPANISH",
  type: EventType,
  ceStates: State[],
): string => {
  if (!isPdWorkshop(type)) {
    return "";
  }
  const copy = workshopCeApplicableCopy(ceStates, language);
  return copy ? ` ${copy}` : "";
};

export const buildWorkshopShareMessage = (
  workshopTitle: string,
  registrationUrl: string,
  language: "ENGLISH" | "SPANISH",
  type: EventType,
  ceStates: State[] = [],
): string => {
  const ceSentence = workshopShareCeSentence(language, type, ceStates);
  if (language === "SPANISH") {
    return `${workshopShareIntro(language, type)}: "${workshopTitle}".${ceSentence} Regístrese aquí: ${registrationUrl}`;
  }
  return `${workshopShareIntro(language, type)}: "${workshopTitle}".${ceSentence} Sign up here: ${registrationUrl}`;
};

type WorkshopShareEmail = { subject: string; text: string; html: string };

export const buildWorkshopShareEmail = (
  workshopTitle: string,
  registrationUrl: string,
  language: "ENGLISH" | "SPANISH",
  type: EventType,
  ceStates: State[] = [],
): WorkshopShareEmail => {
  const p = 'style="margin: 0 0 16px 0; font-size: 16px; color: #333333;"';
  const safeTitle = escapeHtml(workshopTitle);
  const safeUrl = escapeAttr(registrationUrl);
  const intro = workshopShareIntro(language, type);
  const ceCopy = isPdWorkshop(type)
    ? workshopCeApplicableCopy(ceStates, language)
    : null;
  const ceHtml = ceCopy ? `<p ${p}>${escapeHtml(ceCopy)}</p>` : "";
  if (language === "SPANISH") {
    return {
      subject: isPdWorkshop(type)
        ? `Taller gratuito de un colega: ${workshopTitle}`
        : `Taller gratuito de su escuela: ${workshopTitle}`,
      text: `${intro}: "${workshopTitle}".${ceCopy ? `\n\n${ceCopy}` : ""}\n\nRegístrese aquí: ${registrationUrl}`,
      html: `<p ${p}>${intro}: &ldquo;${safeTitle}&rdquo;.</p>${ceHtml}<p ${p}><a href="${safeUrl}" style="color:#2496C6;">Regístrese aquí</a></p>`,
    };
  }
  return {
    subject: isPdWorkshop(type)
      ? `A free workshop from a colleague: ${workshopTitle}`
      : `A free workshop from your school: ${workshopTitle}`,
    text: `${intro}: "${workshopTitle}".${ceCopy ? `\n\n${ceCopy}` : ""}\n\nSign up here: ${registrationUrl}`,
    html: `<p ${p}>${intro}: &ldquo;${safeTitle}&rdquo;.</p>${ceHtml}<p ${p}><a href="${safeUrl}" style="color:#2496C6;">Sign up here</a></p>`,
  };
};
